import { useId, useRef, useState } from 'react';
import type { DragEvent } from 'react';
import { Upload, X } from 'lucide-react';
import { t } from '../lib/i18n';

/** One size to produce from the chosen image. `fit` keeps the shape inside the box; `cover` fills it exactly (centre crop). */
export interface ImageSize {
  width: number;
  height: number;
  mode?: 'fit' | 'cover';
  /** Output type. Default image/jpeg. */
  type?: 'image/jpeg' | 'image/png';
  /** JPEG quality 0 to 1. Default 0.85. */
  quality?: number;
}

/** Largest size that fits inside the box, keeping the shape. Never enlarges. */
export function fitSize(srcW: number, srcH: number, maxW: number, maxH: number): { width: number; height: number } {
  const scale = Math.min(1, maxW / srcW, maxH / srcH);
  return { width: Math.max(1, Math.round(srcW * scale)), height: Math.max(1, Math.round(srcH * scale)) };
}

/** The centred part of the source that has the target's shape. */
export function coverCrop(srcW: number, srcH: number, dstW: number, dstH: number): { x: number; y: number; width: number; height: number } {
  const srcRatio = srcW / srcH;
  const dstRatio = dstW / dstH;
  if (srcRatio > dstRatio) {
    const width = srcH * dstRatio;
    return { x: (srcW - width) / 2, y: 0, width, height: srcH };
  }
  const height = srcW / dstRatio;
  return { x: 0, y: (srcH - height) / 2, width: srcW, height };
}

/** Resizes an image file in the browser to each requested size. Output order matches `sizes`. */
export async function resizeImage(file: Blob, sizes: readonly ImageSize[]): Promise<Blob[]> {
  const bitmap = await createImageBitmap(file);
  try {
    const out: Blob[] = [];
    for (const s of sizes) {
      const canvas = document.createElement('canvas');
      const type = s.type ?? 'image/jpeg';
      let crop = { x: 0, y: 0, width: bitmap.width, height: bitmap.height };
      if (s.mode === 'cover') {
        canvas.width = s.width;
        canvas.height = s.height;
        crop = coverCrop(bitmap.width, bitmap.height, s.width, s.height);
      } else {
        const f = fitSize(bitmap.width, bitmap.height, s.width, s.height);
        canvas.width = f.width;
        canvas.height = f.height;
      }
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('canvas unavailable');
      if (type === 'image/jpeg') {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      ctx.drawImage(bitmap, crop.x, crop.y, crop.width, crop.height, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, s.quality ?? 0.85));
      if (!blob) throw new Error('encode failed');
      out.push(blob);
    }
    return out;
  } finally {
    bitmap.close();
  }
}

function formatBytes(n: number): string {
  if (n >= 1024 * 1024) return `${Math.round((n / (1024 * 1024)) * 10) / 10} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
}

export interface FileDropResult {
  /** The file as chosen. */
  file: File;
  /** One Blob per entry of `sizes` (in that order), or `[file]` when no sizes were asked for. */
  blobs: Blob[];
}

/**
 * Choose or drop one file. With `sizes` set it resizes the image in the browser and returns a Blob for
 * each size (e.g. a 960 px photo and a 160 px thumbnail). The input is a real file field, so it works
 * from the keyboard and with assistive technology.
 */
export function FileDrop({
  label,
  accept = ['image/jpeg', 'image/png'],
  maxBytes = 10 * 1024 * 1024,
  sizes,
  onChange,
  disabled = false,
}: {
  label: string;
  /** Allowed MIME types. */
  accept?: readonly string[];
  /** Largest file accepted before any resizing. */
  maxBytes?: number;
  sizes?: readonly ImageSize[];
  /** Called with the result, or null when the file is removed. */
  onChange: (result: FileDropResult | null) => void;
  disabled?: boolean;
}) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const [name, setName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);

  const take = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    if (!accept.includes(file.type)) {
      setError(t('widget.file.wrongType'));
      return;
    }
    if (file.size > maxBytes) {
      setError(t('widget.file.tooBig', { limit: formatBytes(maxBytes) }));
      return;
    }
    setBusy(true);
    try {
      const blobs = sizes && sizes.length > 0 ? await resizeImage(file, sizes) : [file];
      setName(file.name);
      onChange({ file, blobs });
    } catch {
      setError(t('widget.file.unreadable'));
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setOver(false);
    if (!disabled) void take(e.dataTransfer.files[0]);
  };

  const clear = () => {
    setName(null);
    setError(null);
    if (input.current) input.current.value = '';
    onChange(null);
  };

  return (
    <div>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
        className={`rounded-xl border-2 border-dashed p-5 text-center ${over ? 'border-brand bg-brand-soft' : 'border-slate-300 bg-white'}`}
      >
        <Upload aria-hidden="true" className="mx-auto h-6 w-6 text-slate-500" />
        <p className="mt-2 text-sm text-slate-700">{t('widget.file.drop')}</p>
        <label
          htmlFor={id}
          className="mt-3 inline-flex min-h-9 cursor-pointer items-center rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-800 hover:bg-slate-50 has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-brand"
        >
          <span className="sr-only">{label}: </span>
          {name ? t('widget.file.replace') : t('widget.file.choose')}
          <input
            ref={input}
            id={id}
            type="file"
            className="sr-only"
            accept={accept.join(',')}
            disabled={disabled || busy}
            onChange={(e) => void take(e.target.files?.[0])}
          />
        </label>
      </div>
      {name ? (
        <p className="mt-2 flex items-center gap-2 text-sm text-slate-700">
          <span className="min-w-0 truncate">{t('widget.file.selected', { name })}</span>
          <button
            type="button"
            aria-label={t('widget.file.remove')}
            onClick={clear}
            disabled={disabled}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-600 hover:bg-slate-100"
          >
            <X aria-hidden="true" className="h-4 w-4" />
          </button>
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="mt-2 text-sm text-red-700">
          {error}
        </p>
      ) : null}
    </div>
  );
}
